/**
 * client/src/services/offlineCache.ts
 *
 * FASE 3 — Offline-First Cache con Dexie (IndexedDB) (Luglio 2026).
 *
 * Fornisce una cache persistente per dati frequentemente acceduti,
 * permettendo all'app di funzionare anche senza connessione di rete.
 *
 * ARCHITETTURA:
 * - Dexie (IndexedDB wrapper) come storage layer
 * - TTL configurabile per ogni tipo di dato
 * - Stale-while-revalidate: restituisce dati cached subito,
 *   poi aggiorna in background
 * - Versionamento automatico: pulisce la cache quando cambia
 *   la versione dell'app
 *
 * COSA CACHIAMO:
 * - Profilo utente (TTL: 30 min)
 * - Lista eventi nearby (TTL: 5 min)
 * - Garage/moto (TTL: 30 min)
 * - Classifiche (TTL: 15 min)
 * - Badge (TTL: 1 ora)
 * - Preferenze utente (TTL: 1 ora)
 * - Configurazione feature flags (TTL: 1 ora)
 *
 * USAGE:
 *   import { cachedFetch } from '@/services/offlineCache';
 *   const events = await cachedFetch('/api/events/nearby', { ttl: 5 * 60_000 });
 */

import Dexie, { type Table } from 'dexie';
import { resolveApiBaseUrl } from '@/utils/apiBase';

// ─── Database Schema ─────────────────────────────────────────────────────────

interface CacheEntry {
  key: string;        // URL o chiave univoca
  value: unknown;     // Dato serializzato (JSON)
  cachedAt: number;   // Timestamp Unix ms
  ttl: number;        // TTL in ms
  version: number;    // Schema version per invalidazione
}

class OfflineCacheDB extends Dexie {
  cache!: Table<CacheEntry, string>;

  constructor() {
    super('RideConnectOfflineCache');
    this.version(1).stores({
      cache: '&key, cachedAt, version',
    });
  }
}

const db = new OfflineCacheDB();

// ─── Cache version (incrementa per invalidare tutta la cache) ────────────────

const CACHE_VERSION = 1;

// ─── Public API ──────────────────────────────────────────────────────────────

export interface CachedFetchOptions {
  /** TTL in millisecondi (default: 5 minuti) */
  ttl?: number;
  /** Se true, forza il refresh dalla rete ignorando la cache */
  forceRefresh?: boolean;
  /** Se true, non lancia errore se offline — restituisce dati cached anche scaduti */
  offlineFallback?: boolean;
}

/**
 * Esegue una fetch con cache IndexedDB.
 *
 * Strategia stale-while-revalidate:
 * 1. Controlla se c'è un dato in cache non scaduto → lo restituisce subito
 * 2. In background, fa la fetch dalla rete e aggiorna la cache
 * 3. Se la fetch fallisce e offlineFallback=true, restituisce dati cached anche scaduti
 *
 * @param url     URL da fetchare (usato come chiave di cache)
 * @param options Opzioni di cache
 * @returns       Promise<T> con il dato
 */
export async function cachedFetch<T = unknown>(
  url: string,
  options: CachedFetchOptions = {}
): Promise<T> {
  const { ttl = 5 * 60_000, forceRefresh = false, offlineFallback = true } = options;

  // 1. Prova cache (se non forceRefresh)
  if (!forceRefresh) {
    const cached = await db.cache.get(url);
    if (cached && cached.version === CACHE_VERSION) {
      const age = Date.now() - cached.cachedAt;
      if (age < cached.ttl) {
        // Cache valida — restituisci subito, aggiorna in background
        scheduleBackgroundRefresh(url, ttl);
        return cached.value as T;
      }
    }
  }

  // 2. Fetch dalla rete
  try {
    const data = await fetchFromNetwork<T>(url);
    // Salva in cache
    await db.cache.put({
      key: url,
      value: data,
      cachedAt: Date.now(),
      ttl,
      version: CACHE_VERSION,
    });
    return data;
  } catch (err) {
    // 3. Fallback offline
    if (offlineFallback) {
      const stale = await db.cache.get(url);
      if (stale) {
        console.warn(`[OfflineCache] Serving stale data for: ${url}`);
        return stale.value as T;
      }
    }
    throw err;
  }
}

/**
 * Pre-carica dati nella cache (es. all'avvio dell'app per dati critici).
 * Utile per garantire che la dashboard sia visibile anche offline.
 */
export async function preloadCache(url: string, ttl?: number): Promise<void> {
  try {
    await cachedFetch(url, { ttl, forceRefresh: true, offlineFallback: false });
  } catch {
    // Preload fallito — non critico, la cache proverà al prossimo accesso
  }
}

/**
 * Invalida una chiave di cache specifica.
 */
export async function invalidateCache(key: string): Promise<void> {
  await db.cache.delete(key);
}

/**
 * Invalida tutte le chiavi che iniziano con un prefisso.
 * Utile per pulire cache di una risorsa dopo una mutazione.
 *
 * @example
 *   // Dopo aver modificato il garage, invalida tutte le cache correlate
 *   await invalidateCacheByPrefix('/api/garage');
 */
export async function invalidateCacheByPrefix(prefix: string): Promise<void> {
  const keys = await db.cache
    .where('key')
    .startsWith(prefix)
    .primaryKeys();
  await db.cache.bulkDelete(keys);
}

/**
 * Pulisce tutte le entry scadute.
 * Chiamare periodicamente (es. una volta al giorno).
 */
export async function cleanupExpiredCache(): Promise<number> {
  const now = Date.now();
  const expired = await db.cache
    .filter((entry) => {
      return entry.version !== CACHE_VERSION || (now - entry.cachedAt) > entry.ttl;
    })
    .primaryKeys();
  await db.cache.bulkDelete(expired);
  return expired.length;
}

/**
 * Restituisce statistiche sulla cache.
 */
export async function getCacheStats(): Promise<{
  totalEntries: number;
  totalSizeBytes: number;
  oldestEntry: number | null;
}> {
  const entries = await db.cache.toArray();
  const totalSize = entries.reduce((sum, e) => {
    return sum + (typeof e.value === 'string' ? e.value.length : JSON.stringify(e.value).length);
  }, 0);
  const oldest = entries.length > 0
    ? Math.min(...entries.map((e) => e.cachedAt))
    : null;

  return {
    totalEntries: entries.length,
    totalSizeBytes: totalSize,
    oldestEntry: oldest,
  };
}

// ─── Internal helpers ────────────────────────────────────────────────────────

async function fetchFromNetwork<T>(url: string): Promise<T> {
  const API_BASE = resolveApiBaseUrl();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // Includi token di autenticazione se disponibile
  try {
    const { useAuthStore } = await import('@/store/useAuthStore');
    const token = useAuthStore.getState().accessToken;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // Auth store non disponibile (es. Service Worker) — procedi senza token
  }
  const res = await fetch(`${API_BASE}${url}`, {
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    throw new Error(`[OfflineCache] Network error: ${res.status} ${res.statusText}`);
  }

  return res.json() as Promise<T>;
}

// In-flight dedup: evita N refresh paralleli della stessa URL
const _pendingRefreshes = new Map<string, Promise<void>>();

function scheduleBackgroundRefresh(url: string, ttl: number): void {
  if (_pendingRefreshes.has(url)) return;

  const promise = (async () => {
    try {
      const data = await fetchFromNetwork(url);
      await db.cache.put({
        key: url,
        value: data,
        cachedAt: Date.now(),
        ttl,
        version: CACHE_VERSION,
      });
    } catch {
      // Background refresh fallito — userà dati cached finché non scadono
    } finally {
      _pendingRefreshes.delete(url);
    }
  })();

  _pendingRefreshes.set(url, promise);
}

// ─── Preload critico all'avvio ────────────────────────────────────────────────

/**
 * Pre-carica i dati essenziali per la dashboard.
 * Chiamare UNA volta dopo il login.
 */
export async function preloadCriticalData(): Promise<void> {
  // Only preload if we have a valid token
  let token: string | null = null;
  let userType: string | undefined;
  try {
    const { useAuthStore } = await import('@/store/useAuthStore');
    const state = useAuthStore.getState();
    token = state.accessToken;
    userType = state.user?.user_type as string | undefined;
    // Skip preload if not authenticated or token missing
    if (!state.user || !token) return;
  } catch {
    return;
  }

  const criticalPaths = [
    { url: '/api/profile/me', ttl: 30 * 60_000 },
    { url: '/api/garage/overview', ttl: 30 * 60_000 },
    // FIX (Ago 2026): /api/users/me/badges è biker-only (denyRole dealer)
    // -> 403 per dealer/admin e rumore in console. Skip per ruoli non-biker.
    ...(userType === 'private' || !userType
      ? [{ url: '/api/users/me/badges', ttl: 60 * 60_000 }]
      : []),
  ];

  await Promise.allSettled(
    criticalPaths.map(({ url, ttl }) => preloadCache(url, ttl))
  );
}
